import React from "react";
import HeroSection from "../components/HeroSection";
import { heroSections } from "../constants/data.ts";
import images from "../constants/images.ts";

const aiDataServices = [
  {
    title: "Annotation",
    description:
      "Transform raw data into accurate, AI-ready datasets through expert labeling, review, and quality control workflows.",
    listTitle: "Our Annotation Services Include:",
    image: images.aiDataOperationServices.annotation,
    items: [
      "Image Annotation",
      "Video Annotation",
      "Text Annotation",
      "Audio Annotation",
      "Object Detection & Segmentation",
      "Bounding Boxes & Polygon Annotation",
      "Named Entity Recognition (NER)",
      "Sentiment & Intent Annotation",
      "Data Validation & Quality Review",
      "Custom Labeling Guidelines",
    ],
    benefits: [
      "Higher Model Accuracy",
      "Consistent Training Data",
      "Improved Computer Vision Performance",
      "Reduced Data Preparation Time",
      "Scalable Annotation Support",
    ],
  },
  {
    title: "Transcription",
    description:
      "Convert speech, audio, and video content into structured, high-quality text for training speech recognition, analytics, and language models.",
    listTitle: "Our Transcription Services Include:",
    image: images.aiDataOperationServices.transcription,
    items: [
      "Audio Transcription",
      "Video Transcription",
      "Multilingual Transcription",
      "Speaker Identification",
      "Timestamping",
      "Caption & Subtitle Creation",
      "Verbatim & Clean Transcription",
      "Quality Review & Validation",
    ],
    benefits: [
      "Accurate Speech-to-Text Data",
      "Better Voice Model Training",
      "Support for Multiple Languages",
      "Improved Searchability",
      "Reliable Data Quality",
    ],
  },
  {
    title: "RLHF",
    description:
      "Enhance AI models with expert human feedback to make outputs more accurate, reliable, safe, and aligned with real user expectations.",
    listTitle: "Our RLHF Services Include:",
    image: images.aiDataOperationServices.rlhf,
    items: [
      "Prompt Evaluation",
      "Response Ranking",
      "Preference Comparison",
      "Quality Assessment",
      "Safety & Bias Evaluation",
      "Human Review & Validation",
      "AI Response Optimization",
      "Model Output Scoring",
    ],
    benefits: [
      "Improved Model Alignment",
      "Higher Response Quality",
      "Reduced Bias & Risk",
      "Better User Experience",
      "More Reliable AI Outputs",
    ],
  },
  {
    title: "Voice & Video Dataset Creation",
    description:
      "Build diverse, high-quality voice and video datasets for training next-generation AI systems across speech, vision, and multimodal use cases.",
    listTitle: "Our Dataset Creation Services Include:",
    image: images.aiDataOperationServices.voiceVideoDatasetCreation,
    items: [
      "Voice Recording",
      "Speech Collection",
      "Accent & Dialect Coverage",
      "Emotion-Based Speech Collection",
      "Video Recording",
      "Facial Expression Dataset Creation",
      "Gesture & Action Dataset Collection",
      "Metadata Generation & Quality Validation",
    ],
    benefits: [
      "Diverse Training Data",
      "Better Speech Recognition",
      "Improved Video Analytics",
      "Custom Dataset Collection",
      "Quality-Controlled Data Delivery",
    ],
  },
];

const processSteps = [
  "Requirement Analysis",
  "Data Scope Planning",
  "Guideline Preparation",
  "Resource Allocation",
  "Data Collection & Processing",
  "Quality Review",
  "Validation & Reporting",
  "Secure Delivery",
];

const reasons = [
  "Experienced Data Operations Team",
  "Human-in-the-Loop Workflows",
  "Scalable Delivery Capacity",
  "Strict Quality Control",
  "Custom Project Guidelines",
  "Multilingual Data Support",
  "Secure Data Handling",
  "Transparent Reporting",
  "Flexible Engagement Models",
  "Fast Turnaround Time",
];

const industries = [
  "Artificial Intelligence",
  "Healthcare",
  "Automotive",
  "Retail & E-commerce",
  "Banking & Finance",
  "Media & Entertainment",
  "Education",
  "Telecommunications",
  "Security & Surveillance",
  "Customer Support",
  "Travel & Hospitality",
  "Startups & Enterprises",
];

const AIDataOperations = () => {
  return (
    <main className="bg-background">
      <HeroSection {...heroSections.aiDataOperations} />

      <section className="bg-white px-outer py-16 md:py-24">
        <div className="mx-auto w-full max-w-6xl">
          <p className="mb-3 heading-main text-primary">
            AI Data Operations
          </p>
          <h2 className="heading-main mb-5">
            Build Better AI with High-Quality Human-Labeled Data
          </h2>
          <div className="space-y-4">
            <p className="text-body">
              AI models are only as strong as the data used to train, test, and
              refine them. At Westlisoft, we provide reliable AI data
              operations services that help businesses prepare accurate,
              structured, and scalable datasets for machine learning and
              generative AI systems.
            </p>
            <p className="text-body">
              Our team combines trained human reviewers, clear labeling
              guidelines, quality checks, and secure delivery workflows to
              support annotation, transcription, RLHF, and custom voice and
              video dataset creation.
            </p>
            <p className="text-body">
              Whether you are building computer vision models, speech AI,
              chatbots, large language models, or multimodal AI products, we
              help you improve model accuracy, consistency, and real-world
              performance.
            </p>
          </div>
        </div>
      </section>

      <section className="px-outer py-16 md:pb-24">
        <div className="mx-auto w-full max-w-6xl">
          <div className="mb-12 max-w-3xl">
            <p className="mb-3 heading-main text-primary">
              Our AI Data Operations Services
            </p>
          </div>

          <div className="space-y-10 md:space-y-14">
            {aiDataServices.map((service, index) => {
              const imageFirst = index % 2 === 1;

              return (
                <article
                  key={service.title}
                  className="grid items-center gap-6 rounded-card bg-white p-5 shadow-sm md:grid-cols-2 md:gap-10 lg:p-8"
                >
                  <div
                    className={
                      imageFirst
                        ? "order-2 md:order-2"
                        : "order-2 md:order-1"
                    }
                  >
                    <h3 className="mb-4 font-rubik text-2xl font-bold text-secondary md:text-3xl">
                      {service.title}
                    </h3>
                    <p className="text-body mb-5">{service.description}</p>
                    <h4 className="mb-3 font-rubik text-lg font-semibold text-secondary">
                      {service.listTitle}
                    </h4>
                    <ul className="mb-5 grid gap-2 text-sm font-light text-gray-700 sm:grid-cols-2">
                      {service.items.map((item) => (
                        <li key={item} className="flex gap-2">
                          <span className="mt-2 h-1.5 w-1.5 shrink-0 rounded-full bg-primary" />
                          <span>{item}</span>
                        </li>
                      ))}
                    </ul>
                    <h4 className="mb-3 font-rubik text-lg font-semibold text-secondary">
                      Benefits
                    </h4>
                    <ul className="grid gap-2 text-sm font-light text-gray-700 sm:grid-cols-2">
                      {service.benefits.map((benefit) => (
                        <li key={benefit} className="flex gap-2">
                          <span className="mt-2 h-1.5 w-1.5 shrink-0 rounded-full bg-primary" />
                          <span>{benefit}</span>
                        </li>
                      ))}
                    </ul>
                  </div>

                  <div
                    className={
                      imageFirst
                        ? "order-1 flex h-60 items-center justify-center rounded-card bg-gray-50 p-3 sm:h-72 md:order-1 md:h-[320px] lg:h-[360px]"
                        : "order-1 flex h-60 items-center justify-center rounded-card bg-gray-50 p-3 sm:h-72 md:order-2 md:h-[320px] lg:h-[360px]"
                    }
                  >
                    <img
                      src={service.image}
                      alt={service.title}
                      className="h-full w-full object-contain"
                    />
                  </div>
                </article>
              );
            })}
          </div>
        </div>
      </section>

      <section className="bg-white px-outer py-16 md:py-24">
        <div className="mx-auto w-full max-w-6xl text-center">
          <p className="mb-4 heading-main text-primary">
            Our Data Operations Process
          </p>
          <p className="text-body mx-auto mb-8 max-w-3xl">
            We follow a structured delivery process to ensure every dataset is
            accurate, consistent, secure, and ready for AI model training.
          </p>
          <ol className="mx-auto grid max-w-5xl gap-4 sm:grid-cols-2 lg:grid-cols-4">
            {processSteps.map((step, index) => (
              <li
                key={step}
                className="flex min-h-24 flex-col items-center justify-center rounded-card bg-background px-4 py-5 text-center font-rubik text-sm font-light text-gray-700"
              >
                <span className="mb-3 flex h-9 w-9 items-center justify-center rounded-full bg-primary font-bold text-secondary">
                  {index + 1}
                </span>
                <span>{step}</span>
              </li>
            ))}
          </ol>
        </div>
      </section>

      <section className="px-outer py-16 md:py-24">
        <div className="mx-auto w-full max-w-6xl text-center">
          <h2 className="heading-main mb-8 text-primary">
            Why Choose Westlisoft?
          </h2>
          <ul className="mx-auto grid max-w-5xl gap-4 sm:grid-cols-2 lg:grid-cols-5">
            {reasons.map((reason) => (
              <li
                key={reason}
                className="flex min-h-20 items-center justify-center rounded-card bg-white px-4 py-4 text-center font-rubik text-sm font-light text-gray-700 shadow-sm"
              >
                {reason}
              </li>
            ))}
          </ul>
        </div>
      </section>

      <section className="bg-white px-outer py-16 md:py-24">
        <div className="mx-auto w-full max-w-6xl text-center">
          <p className="mb-4 heading-main text-primary">
            Industries We Serve
          </p>
          <p className="text-body mx-auto mb-8 max-w-3xl">
            We support AI data operations for organizations across multiple
            industries, including:
          </p>
          <ul className="mx-auto grid max-w-5xl gap-4 sm:grid-cols-2 lg:grid-cols-4">
            {industries.map((industry) => (
              <li
                key={industry}
                className="flex min-h-16 items-center justify-center rounded-card bg-background px-4 py-4 text-center font-rubik text-sm font-light text-gray-700"
              >
                {industry}
              </li>
            ))}
          </ul>
        </div>
      </section>

      <section className="px-outer py-16 md:py-24">
        <div className="mx-auto w-full max-w-5xl text-center">
          <h2 className="heading-main mb-4 text-primary">
            Ready to Improve Your AI Data Pipeline?
          </h2>
          <p className="text-body mx-auto max-w-4xl">
            Partner with Westlisoft to prepare accurate, scalable, and
            quality-controlled datasets for your AI initiatives. From annotation
            and transcription to RLHF and custom dataset creation, our data
            operations team helps you build stronger AI systems with confidence.
          </p>
        </div>
      </section>
    </main>
  );
};

export default AIDataOperations;
